import { ensureSubscriptionActionAllowed } from '../../utils/subscriptionActionGuard';
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  Form,
  DatePicker,
  Select,
  Checkbox,
  Button,
  Row,
  Col,
  Card,
  Statistic,
  Divider,
  Alert,
  Progress,
  Space,
  Typography,
  Tag,
  Tooltip,
  message
} from "antd";
import {
  DownloadOutlined,
  FileExcelOutlined,
  CalendarOutlined,
  FilterOutlined,
  BarChartOutlined,
  TeamOutlined,
  ShopOutlined,
  CloseOutlined,
  CheckCircleOutlined
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";
import API_CONFIG from "../../config/api.js";
import { deduplicateEntities } from "../../utils/entityUtils";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const API_URL_EXPENSES = API_CONFIG.apiUrl("/expenses");
const API_URL_CATEGORIES = API_CONFIG.apiUrl("/expenses/categories");
const API_URL_SUPPLIERS = API_CONFIG.apiUrl("/users/suppliers");
const MAX_EXPORT_RANGE_MS = 2 * 365 * 24 * 60 * 60 * 1000;
const RANGE_LIMIT_MESSAGE = "La période d'export ne peut pas dépasser 2 ans.";

const ExpenseExportModal = ({
  visible,
  onClose,
  existingFilters = {},
  moduleScope = "commercial",
}) => {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { user } = useAuth();
  const [form] = Form.useForm();

  // États locaux
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [exportProgress, setExportProgress] = useState(0);
  const [previewStats, setPreviewStats] = useState(null);
  const progressIntervalRef = useRef(null);
  const [exportOptions, setExportOptions] = useState({
    dateRange: [dayjs().subtract(30, 'days'), dayjs()],
    categories: [],
    suppliers: [],
    warehouses: selectedWarehouse ? [selectedWarehouse] : [],
    groupBy: 'category',
    includeDocuments: true,
    includeSupplierAnalysis: false
  });

  const clearProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Chargement des données de référence
  useEffect(() => {
    if (visible && selectedCompany) {
      loadReferenceData();
      initializeFilters();
    }
  }, [visible, selectedCompany, moduleScope]);

  // Mise à jour de l'aperçu quand les options changent
  useEffect(() => {
    if (visible && exportOptions.dateRange[0] && exportOptions.dateRange[1]) {
      const timer = setTimeout(() => {
        loadPreview();
      }, 500); // Débounce de 500ms

      return () => clearTimeout(timer);
    }
  }, [
    visible,
    exportOptions.dateRange,
    exportOptions.categories,
    exportOptions.suppliers,
    exportOptions.warehouses,
    moduleScope,
  ]);

  useEffect(() => {
    if (!visible) {
      clearProgressInterval();
    }
  }, [visible, clearProgressInterval]);

  useEffect(() => {
    return () => {
      clearProgressInterval();
    };
  }, [clearProgressInterval]);

  const loadReferenceData = async () => {
    try {
      const [categoriesRes, suppliersRes] = await Promise.all([
        axios.get(API_URL_CATEGORIES, {
          params: { company_id: selectedCompany, module_scope: moduleScope },
        }),
        axios.get(API_URL_SUPPLIERS, { params: { companyId: selectedCompany } })
      ]);

      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      const supplierRows = Array.isArray(suppliersRes.data) ? suppliersRes.data : [];
      setSuppliers(deduplicateEntities(supplierRows));
    } catch (error) {
      message.error("Erreur lors du chargement des données de référence");
      console.error("Error loading reference data:", error);
    }
  };

  const buildExistingDateRange = () => {
    if (Array.isArray(existingFilters.dateRange) && existingFilters.dateRange.length === 2) {
      const [start, end] = existingFilters.dateRange;
      const startDate = dayjs.isDayjs(start) ? start : dayjs(start);
      const endDate = dayjs.isDayjs(end) ? end : dayjs(end);
      if (startDate.isValid() && endDate.isValid()) {
        return [startDate, endDate];
      }
    }

    if (existingFilters.startDate && existingFilters.endDate) {
      const startDate = dayjs(existingFilters.startDate);
      const endDate = dayjs(existingFilters.endDate);
      if (startDate.isValid() && endDate.isValid()) {
        return [startDate, endDate];
      }
    }

    return exportOptions.dateRange;
  };

  const initializeFilters = () => {
    // Initialiser avec les filtres existants de la page si disponibles
    const initialOptions = {
      ...exportOptions,
      dateRange: buildExistingDateRange(),
      categories: existingFilters.categoryIds || [],
      suppliers: existingFilters.supplierIds || [],
      warehouses: existingFilters.warehouseId ? [existingFilters.warehouseId] :
                  selectedWarehouse ? [selectedWarehouse] : []
    };

    setExportOptions(initialOptions);

    form.setFieldsValue({
      dateRange: initialOptions.dateRange,
      categories: initialOptions.categories,
      suppliers: initialOptions.suppliers,
      warehouses: initialOptions.warehouses,
      groupBy: initialOptions.groupBy,
      includeDocuments: initialOptions.includeDocuments,
      includeSupplierAnalysis: initialOptions.includeSupplierAnalysis
    });
  };

  const getNormalizedRange = (range) => {
    if (!Array.isArray(range) || range.length !== 2) return null;
    const [start, end] = range;
    if (!start || !end) return null;
    return [start.startOf('day'), end.endOf('day')];
  };

  const isRangeExceedingLimit = (range) => {
    const normalized = getNormalizedRange(range);
    if (!normalized) return false;
    const [start, end] = normalized;
    return end.valueOf() - start.valueOf() > MAX_EXPORT_RANGE_MS;
  };

  const loadPreview = async () => {
    if (!exportOptions.dateRange[0] || !exportOptions.dateRange[1]) return;
    if (isRangeExceedingLimit(exportOptions.dateRange)) {
      setPreviewStats(null);
      return;
    }

    const normalizedRange = getNormalizedRange(exportOptions.dateRange);
    if (!normalizedRange) return;
    const [normalizedStart, normalizedEnd] = normalizedRange;

    setLoadingPreview(true);
    try {
      const params = {
        companyId: selectedCompany,
        startDate: normalizedStart.toISOString(),
        endDate: normalizedEnd.toISOString(),
        page: 1,
        limit: 1, // On veut juste le count
        module_scope: moduleScope,
      };

      // Ajouter les filtres
      if (exportOptions.warehouses.length > 0) {
        params.warehouseId = exportOptions.warehouses[0]; // API actuelle ne supporte qu'un magasin
      }
      if (exportOptions.categories.length > 0) {
        params.categoryId = exportOptions.categories[0]; // Simplification pour l'aperçu
      }
      if (exportOptions.suppliers.length > 0) {
        params.supplierId = exportOptions.suppliers[0]; // Simplification pour l'aperçu
      }

      const response = await axios.get(API_URL_EXPENSES, { params });

      const stats = {
        totalExpenses: response.data?.pagination?.totalItems || 0,
        totalAmount: 0 // On pourrait améliorer cela en ajoutant un endpoint de stats
      };

      // Calculer le total si on a des dépenses
      if (response.data?.expenses && response.data.expenses.length > 0) {
        // Faire une requête sans limite pour obtenir toutes les données pour le calcul
        const fullParams = { ...params, limit: 10000 };
        const fullResponse = await axios.get(API_URL_EXPENSES, { params: fullParams });

        stats.totalAmount = fullResponse.data?.expenses?.reduce(
          (sum, expense) => sum + parseFloat(expense.amount || 0), 0
        ) || 0;
      }

      setPreviewStats(stats);
    } catch (error) {
      console.error("Error loading preview:", error);
      setPreviewStats({ totalExpenses: 0, totalAmount: 0 });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleFormValuesChange = (changedValues, allValues) => {
    const newOptions = { ...exportOptions };

    if (changedValues.dateRange) {
      newOptions.dateRange = changedValues.dateRange;
    }
    if (changedValues.categories !== undefined) {
      newOptions.categories = changedValues.categories || [];
    }
    if (changedValues.suppliers !== undefined) {
      newOptions.suppliers = changedValues.suppliers || [];
    }
    if (changedValues.warehouses !== undefined) {
      newOptions.warehouses = changedValues.warehouses || [];
    }
    if (changedValues.groupBy !== undefined) {
      newOptions.groupBy = changedValues.groupBy;
    }
    if (changedValues.includeDocuments !== undefined) {
      newOptions.includeDocuments = changedValues.includeDocuments;
    }
    if (changedValues.includeSupplierAnalysis !== undefined) {
      newOptions.includeSupplierAnalysis = changedValues.includeSupplierAnalysis;
    }

    setExportOptions(newOptions);
  };

  const handleExport = async () => {
    if (!(await ensureSubscriptionActionAllowed('data_export'))) return;
    if (!exportOptions.dateRange[0] || !exportOptions.dateRange[1]) {
      message.error("Veuillez sélectionner une période");
      return;
    }

    if (isRangeExceedingLimit(exportOptions.dateRange)) {
      message.error(RANGE_LIMIT_MESSAGE);
      return;
    }

    if (previewStats?.totalExpenses === 0) {
      message.warning("Aucune charge admin à exporter pour les critères sélectionnés");
      return;
    }

    setLoading(true);
    setExportProgress(0);

    const normalizedRange = getNormalizedRange(exportOptions.dateRange);
    if (!normalizedRange) {
      message.error("Veuillez sélectionner une période valide");
      setLoading(false);
      return;
    }
    const [normalizedStart, normalizedEnd] = normalizedRange;

    try {
      // Simulation du progress pour l'UX
      clearProgressInterval();
      progressIntervalRef.current = setInterval(() => {
        setExportProgress(prev => {
          if (prev < 90) return prev + 10;
          return prev;
        });
      }, 200);

      // Construction des paramètres d'export
      const exportParams = {
        companyId: selectedCompany,
        startDate: normalizedStart.toISOString(),
        endDate: normalizedEnd.toISOString(),
        groupBy: exportOptions.groupBy,
        includeDocuments: exportOptions.includeDocuments,
        companyName: user?.company_name || 'Entreprise',
        module_scope: moduleScope,
      };

      // Ajouter les filtres optionnels
      if (exportOptions.warehouses.length > 0) {
        exportParams.warehouseId = exportOptions.warehouses[0];
      }
      if (exportOptions.categories.length > 0) {
        exportParams.categoryIds = exportOptions.categories.join(',');
      }
      if (exportOptions.suppliers.length > 0) {
        exportParams.supplierIds = exportOptions.suppliers.join(',');
      }
      if (exportOptions.includeSupplierAnalysis) {
        exportParams.includeSupplierAnalysis = true;
      }

      // Requête d'export
      const response = await axios.get(`${API_URL_EXPENSES}/export`, {
        params: exportParams,
        responseType: 'blob'
      });

      clearProgressInterval();
      setExportProgress(100);

      // Création et téléchargement du fichier
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Nom du fichier basé sur la période et les filtres
      const startDate = exportOptions.dateRange[0].format('YYYY-MM-DD');
      const endDate = exportOptions.dateRange[1].format('YYYY-MM-DD');
      const companyName = user?.company_name?.replace(/[^a-zA-Z0-9]/g, '') || 'Entreprise';
      link.download = `ChargesAdmin_Export_${companyName}_${startDate}_${endDate}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(`Export Excel généré avec succès ! ${previewStats?.totalExpenses} charges admin exportées.`);

      // Fermer le modal après un délai
      setTimeout(() => {
        handleClose();
      }, 1500);

    } catch (error) {
      clearProgressInterval();
      console.error("Export error:", error);
      let errorMessage = error.response?.data?.error || error.message;
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMessage = parsed?.error || parsed?.message || errorMessage;
        } catch (parseError) {
          // Laisser le message par défaut si parsing impossible
        }
      }
      message.error("Erreur lors de l'export : " + errorMessage);
      setExportProgress(0);
    } finally {
      clearProgressInterval();
      setLoading(false);
    }
  };

  const handleClose = () => {
    clearProgressInterval();
    setExportProgress(0);
    setPreviewStats(null);
    form.resetFields();
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount).replace(",", ".");
  };

  // Options de groupement
  const groupByOptions = [
    { value: 'category', label: 'Par Catégorie', icon: <BarChartOutlined /> },
    { value: 'supplier', label: 'Par Fournisseur', icon: <TeamOutlined /> },
    { value: 'warehouse', label: 'Par Établissement', icon: <ShopOutlined /> }
  ];

  const isRangeTooLarge = isRangeExceedingLimit(exportOptions.dateRange);

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined style={{ color: '#52c41a' }} />
          <span>Export Excel des Charges Admin</span>
        </Space>
      }
      visible={visible}
      onCancel={handleClose}
      width={800}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormValuesChange}
        initialValues={exportOptions}
      >
        {/* Section Période */}
        <Card size="small" className="mb-4">
          <Row align="middle" className="mb-3">
            <Col>
              <Title level={5}>
                <CalendarOutlined className="mr-2" />
                Période d'Export
              </Title>
            </Col>
          </Row>

          <Form.Item
            name="dateRange"
            rules={[
              { required: true, message: "Veuillez sélectionner une période" },
              {
                validator: (_, value) => {
                  if (!value || value.length !== 2) {
                    return Promise.resolve();
                  }
                  if (isRangeExceedingLimit(value)) {
                    return Promise.reject(new Error(RANGE_LIMIT_MESSAGE));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <RangePicker
              format="DD/MM/YYYY"
              placeholder={["Date début", "Date fin"]}
              style={{ width: "100%" }}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
              ranges={{
                "7 derniers jours": [dayjs().subtract(6, 'days'), dayjs()],
                "30 derniers jours": [dayjs().subtract(29, 'days'), dayjs()],
                "3 derniers mois": [dayjs().subtract(2, 'month').startOf('month'), dayjs()],
                "Cette année": [dayjs().startOf('year'), dayjs()],
                "Année dernière": [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')]
              }}
            />
          </Form.Item>

          {/* Aperçu des données */}
          {previewStats && (
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Nombre de charges admin"
                  value={previewStats.totalExpenses}
                  prefix={<BarChartOutlined />}
                  loading={loadingPreview}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Montant total"
                  value={formatCurrency(previewStats.totalAmount)}
                  prefix={<span style={{ color: '#f5222d' }}>💰</span>}
                  loading={loadingPreview}
                />
              </Col>
            </Row>
          )}
        </Card>

        {/* Section Filtres */}
        <Card size="small" className="mb-4">
          <Row align="middle" className="mb-3">
            <Col>
              <Title level={5}>
                <FilterOutlined className="mr-2" />
                Filtres (Optionnel)
              </Title>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            {/* Catégories */}
            <Col span={24}>
              <Form.Item
                name="categories"
                label="Catégories de Charges Admin"
              >
                <Select
                  mode="multiple"
                  placeholder="Toutes les catégories"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {categories.map(category => (
                    <Option key={category.id} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Fournisseurs */}
            <Col span={24}>
              <Form.Item
                name="suppliers"
                label="Fournisseurs"
              >
                <Select
                  mode="multiple"
                  placeholder="Tous les fournisseurs"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {suppliers.map(supplier => (
                    <Option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Section Options d'Export */}
        <Card size="small" className="mb-4">
          <Title level={5} className="mb-3">Options d'Export</Title>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                name="groupBy"
                label="Type d'Analyse"
              >
                <Select>
                  {groupByOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      <Space>
                        {option.icon}
                        {option.label}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="includeDocuments" valuePropName="checked">
                <Checkbox>
                  Inclure les informations sur les documents joints
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="includeSupplierAnalysis" valuePropName="checked">
                <Checkbox>
                  Ajouter une analyse détaillée par fournisseur
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Informations sur le contenu */}
        <Alert
          message="Contenu de l'Export"
          description={
            <div>
              <Text>Le fichier Excel contiendra :</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li><strong>Feuille Synthèse</strong> : Statistiques générales et répartitions</li>
                <li><strong>Feuille Détail</strong> : Liste complète des charges admin</li>
                {exportOptions.groupBy === 'category' && (
                  <li><strong>Feuille Catégories</strong> : Analyse détaillée par catégorie</li>
                )}
                {(exportOptions.groupBy === 'supplier' || exportOptions.includeSupplierAnalysis) && (
                  <li><strong>Feuille Fournisseurs</strong> : Analyse détaillée par fournisseur</li>
                )}
              </ul>
            </div>
          }
          type="info"
          showIcon
          className="mb-4"
        />

        {/* Progress bar pendant l'export */}
        {loading && (
          <div className="mb-4">
            <Progress
              percent={exportProgress}
              status={exportProgress === 100 ? "success" : "active"}
              format={(percent) => `${percent}%${percent === 100 ? ' Terminé!' : ' Export en cours...'}`}
            />
          </div>
        )}

        {/* Actions */}
        <Row justify="end" gutter={8}>
          <Col>
            <Button
              onClick={handleClose}
              disabled={loading}
              icon={<CloseOutlined />}
            >
              Annuler
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={handleExport}
              loading={loading}
              disabled={isRangeTooLarge || !previewStats || previewStats.totalExpenses === 0}
              icon={exportProgress === 100 ? <CheckCircleOutlined /> : <DownloadOutlined />}
              size="large"
            >
              {loading
                ? (exportProgress === 100 ? 'Téléchargé !' : 'Export en cours...')
                : `Exporter ${previewStats?.totalExpenses || 0} charges admin`
              }
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ExpenseExportModal;
